import type { CollectionGeneratorRef } from "../modeling/generators/types.ts";
import { cloneSceneSketchState } from "../modeling/sketch.ts";
import { cloneLoopNormals } from "../modeling/types.ts";
import {
  unwrapBoxProject,
  unwrapBoxProjectPartial,
} from "../modeling/uvUnwrap.ts";
import { DriverGraph } from "../scene-graph/DriverGraph.ts";
import {
  recomputeLoopNormals,
  recomputeLoopNormalsPartial,
  resolveAutoSmoothAngle,
} from "../wasm/normals.ts";
import { allShapeKeySets } from "./blendFileQueries.ts";
import { cloneGroupTransform } from "./groupTransform.ts";
import { clearLiveEditPreview } from "./liveEditPreview.ts";
import {
  cloneSceneObject,
  type SceneObjectCloneCache,
} from "./persistence/cloneSceneObject.ts";
import { restoreSceneObjectMap } from "./restoreSceneObjects.ts";
import type {
  AppState,
  CollectionCutterRef,
  CommandSnapshot,
  EditObjectSnapshot,
  EditSelectionSnapshot,
  GroupTransform,
  InteractionMode,
  ModifierOverride,
  ObjectGeometryHistoryState,
  ObjectGeometrySnapshotDelta,
  SculptDelta,
  SelectionDelta,
  SelectionDeltaState,
  UndoableSnapshot,
  UndoEntry,
  VertexPositionDelta,
  WorkspaceMode,
} from "./storeTypes.ts";
import { UndoManager } from "./UndoManager.ts";

// ---------------------------------------------------------------------------
// Structural-sharing clone cache
//
// When capturing undo snapshots, geometry buffers (and whole `SceneObject`
// references) that haven't changed since the last capture are reused rather
// than deep-copied again. A WeakMap keyed by the *store's* live references
// auto-GCs entries when the store drops them after an immutable update.
//
// The same cache is shared across `cloneSceneObject` (for sceneObjects) and
// the local `cachedClone` calls (for `editMode.curveEditVertices`), which
// is why both helpers live in `persistence/cloneSceneObject.ts`.
// ---------------------------------------------------------------------------

const _cloneCache: SceneObjectCloneCache = new WeakMap();

// Active edit/sculpt objects can keep the same SceneObject and typed-array
// identities while tools mutate geometry contents in place between commands.
// Reusing their cached clones made command pre-snapshots stale and later
// transforms could resurrect older mesh state, so snapshot correctness wins:
// always deep-clone the object currently being edited or sculpted.
function shouldBypassCloneCache(state: AppState, objectId: string): boolean {
  return (
    state.editMode?.objectId === objectId ||
    state.sculptMode?.objectId === objectId
  );
}

function cloneCollectionGeneratorRef(
  ref: CollectionGeneratorRef | undefined,
): CollectionGeneratorRef | undefined {
  if (!ref) return undefined;
  return {
    generatorId: ref.generatorId,
    params: structuredClone(ref.params),
    roleToObjectId: { ...ref.roleToObjectId },
  };
}

function cloneCollectionCutterRef(
  ref: CollectionCutterRef | undefined,
): CollectionCutterRef | undefined {
  if (!ref) return undefined;
  return {
    hostObjectId: ref.hostObjectId,
    cutterObjectIds: new Set(ref.cutterObjectIds),
  };
}

export function cloneWeights(
  w: Map<string, Map<string, number>>,
): Map<string, Map<string, number>> {
  const out = new Map<string, Map<string, number>>();
  for (const [k, v] of w) out.set(k, new Map(v));
  return out;
}

export function cloneBonePoses(
  m: Map<string, Map<string, [number, number, number, number]>>,
): Map<string, Map<string, [number, number, number, number]>> {
  const out = new Map<string, Map<string, [number, number, number, number]>>();
  for (const [objId, bones] of m) {
    const inner = new Map<string, [number, number, number, number]>();
    for (const [name, q] of bones) inner.set(name, [...q]);
    out.set(objId, inner);
  }
  return out;
}

export function cloneOverrides(
  m: Map<string, ModifierOverride[]>,
): Map<string, ModifierOverride[]> {
  const out = new Map<string, ModifierOverride[]>();
  for (const [k, v] of m)
    out.set(
      k,
      v.map((o) => ({
        ...o,
        props: o.props ? { ...o.props } : undefined,
        runtimeModifier: o.runtimeModifier
          ? { ...o.runtimeModifier }
          : undefined,
      })),
    );
  return out;
}

export function captureEditSelectionSnapshot(
  em: NonNullable<AppState["editMode"]>,
): EditSelectionSnapshot {
  return {
    objectId: em.objectId,
    selectedVertices: new Set(em.selectedVertices),
    selectedEdges: new Set(em.selectedEdges),
    selectedFaces: new Set(em.selectedFaces),
    hiddenVertices: new Set(em.hiddenVertices),
    hiddenFaces: new Set(em.hiddenFaces),
    ...(em.curveEditVertices
      ? { curveEditVertices: new Float32Array(em.curveEditVertices) }
      : {}),
    granularity: em.granularity,
    xray: em.xray ?? false,
    showVertexGroupColors: em.showVertexGroupColors ?? true,
    showFaceGroupColors: em.showFaceGroupColors,
    showMaterialSlotColors: em.showMaterialSlotColors,
    isCurve: em.isCurve,
    lastActiveVertex: em.lastActiveVertex,
    lastActiveEdge: em.lastActiveEdge,
    lastActiveFace: em.lastActiveFace,
  };
}

function editModeFromSnapshot(
  snap: EditSelectionSnapshot,
): NonNullable<AppState["editMode"]> {
  return {
    objectId: snap.objectId,
    selectedVertices: new Set(snap.selectedVertices),
    selectedEdges: new Set(snap.selectedEdges),
    selectedFaces: new Set(snap.selectedFaces),
    hiddenVertices: new Set(snap.hiddenVertices),
    hiddenFaces: new Set(snap.hiddenFaces),
    ...(snap.curveEditVertices
      ? { curveEditVertices: new Float32Array(snap.curveEditVertices) }
      : {}),
    granularity: snap.granularity,
    xray: snap.xray,
    showVertexGroupColors: snap.showVertexGroupColors,
    showFaceGroupColors: snap.showFaceGroupColors,
    showMaterialSlotColors: snap.showMaterialSlotColors,
    isCurve: snap.isCurve,
    lastActiveVertex: snap.lastActiveVertex,
    lastActiveEdge: snap.lastActiveEdge,
    lastActiveFace: snap.lastActiveFace,
  };
}

export function isEditObjectSnapshot(
  snap: CommandSnapshot | null | undefined,
): snap is EditObjectSnapshot {
  return !!snap && "type" in snap && snap.type === "edit-object-snapshot";
}

export function isFullSnapshot(
  snap: CommandSnapshot | null | undefined,
): snap is UndoableSnapshot {
  return !!snap && !isEditObjectSnapshot(snap);
}

export function commandSnapshotObject(snap: CommandSnapshot, objectId: string) {
  return isEditObjectSnapshot(snap)
    ? snap.object.id === objectId
      ? snap.object
      : undefined
    : snap.sceneObjects.find((s) => s.id === objectId);
}

export function captureEditObjectSnapshot(
  state: AppState,
  objectId: string,
): EditObjectSnapshot | null {
  const obj = state.sceneObjects.get(objectId);
  const em = state.editMode;
  if (!obj || !em || em.objectId !== objectId) return null;
  return {
    type: "edit-object-snapshot",
    object: cloneSceneObject(obj),
    editSelection: captureEditSelectionSnapshot(em),
    selectedObjectTargets: state.selectedObjectTargets.map((target) => ({
      ...target,
    })),
    cursor3D: {
      position: [
        state.cursor3D.position[0],
        state.cursor3D.position[1],
        state.cursor3D.position[2],
      ],
    },
  };
}

export function applyEditObjectSnapshot(
  state: AppState,
  snap: EditObjectSnapshot,
): Partial<AppState> {
  clearLiveEditPreview(snap.object.id);
  const sceneObjects = new Map(state.sceneObjects);
  sceneObjects.set(snap.object.id, cloneSceneObject(snap.object));
  return {
    activeEditSession: null,
    sceneObjects,
    editMode: editModeFromSnapshot(snap.editSelection),
    mode: "edit" as InteractionMode,
    workspaceMode: "edit" as WorkspaceMode,
    selectedObjectTargets:
      snap.selectedObjectTargets.length > 0
        ? snap.selectedObjectTargets.map((target) => ({ ...target }))
        : [{ kind: "sceneObject", id: snap.object.id }],
    cursor3D: {
      ...state.cursor3D,
      position: [
        snap.cursor3D.position[0],
        snap.cursor3D.position[1],
        snap.cursor3D.position[2],
      ],
    },
  };
}

export function captureObjectGeometryHistoryState(
  state: AppState,
  objectId: string,
): ObjectGeometryHistoryState | null {
  const obj = state.sceneObjects.get(objectId);
  const em = state.editMode;
  if (!obj || !em || em.objectId !== objectId) return null;
  return {
    object: cloneSceneObject(obj),
    editSelection: captureEditSelectionSnapshot(em),
    selectedObjectTargets: state.selectedObjectTargets.map((target) => ({
      ...target,
    })),
  };
}

function applyObjectGeometryHistoryState(
  state: AppState,
  side: ObjectGeometryHistoryState,
): Partial<AppState> {
  clearLiveEditPreview(side.object.id);
  const sceneObjects = new Map(state.sceneObjects);
  sceneObjects.set(side.object.id, cloneSceneObject(side.object));
  return {
    activeEditSession: null,
    sceneObjects,
    editMode: editModeFromSnapshot(side.editSelection),
    mode: "edit" as InteractionMode,
    workspaceMode: "edit" as WorkspaceMode,
    selectedObjectTargets:
      side.selectedObjectTargets.length > 0
        ? side.selectedObjectTargets.map((target) => ({ ...target }))
        : [{ kind: "sceneObject", id: side.object.id }],
  };
}

export function captureSnapshot(state: AppState): UndoableSnapshot {
  // Snapshot group-level transforms per entry
  const groupTransforms = new Map<string, GroupTransform>();
  for (const [entryId, entry] of state.blendFiles) {
    const gt = entry.groupTransform;
    groupTransforms.set(entryId, {
      position: [...gt.position] as [number, number, number],
      rotation: [...gt.rotation] as [number, number, number, number],
      scale: [...gt.scale] as [number, number, number],
    });
  }
  return {
    groupTransforms,
    blendFiles: state.blendFiles,
    shapeKeyWeights: cloneWeights(state.shapeKeyWeights),
    referenceLayers: state.referenceLayers.map((l) => ({
      ...l,
      offset: [...l.offset] as [number, number],
      vanishingLines: l.vanishingLines?.map((vl) => ({
        ...vl,
        start: [...vl.start] as [number, number],
        end: [...vl.end] as [number, number],
      })),
    })),
    activeReferenceLayerId: state.activeReferenceLayerId,
    modifierOverrides: cloneOverrides(state.modifierOverrides),
    sceneObjects: [...state.sceneObjects.values()].map((o) =>
      shouldBypassCloneCache(state, o.id)
        ? cloneSceneObject(o)
        : cloneSceneObject(o, _cloneCache),
    ),
    materials: [...state.materials.values()].map((m) => ({
      id: m.id,
      name: m.name,
      properties: { ...m.properties },
    })),
    mannequinMorphs: cloneWeights(state.mannequinMorphs),
    mannequinBonePoses: cloneBonePoses(state.mannequinBonePoses),
    visibility: new Map(state.visibility),
    lockedBones: new Map(
      [...state.lockedBones].map(([k, v]) => [k, new Set(v)]),
    ),
    pinnedBones: new Map(
      [...state.pinnedBones].map(([k, v]) => [
        k,
        new Map(
          [...v].map(([b, p]) => [b, [...p] as [number, number, number]]),
        ),
      ]),
    ),
    boneVolumes: cloneWeights(state.boneVolumes),
    collections: [...state.collections.values()].map((c) => ({
      id: c.id,
      name: c.name,
      transform: cloneGroupTransform(c.transform),
      objectIds: [...c.objectIds],
      cutterRef: cloneCollectionCutterRef(c.cutterRef),
      generatorRef: cloneCollectionGeneratorRef(c.generatorRef),
    })),
    selectedObjectTargets: state.selectedObjectTargets.map((target) => ({
      ...target,
    })),
    editSelection: state.editMode
      ? {
          objectId: state.editMode.objectId,
          selectedVertices: new Set(state.editMode.selectedVertices),
          selectedEdges: new Set(state.editMode.selectedEdges),
          selectedFaces: new Set(state.editMode.selectedFaces),
          hiddenVertices: new Set(state.editMode.hiddenVertices),
          hiddenFaces: new Set(state.editMode.hiddenFaces),
          // Active curve-edit buffers are part of the same live-edit surface area
          // as the mesh vertex scratch: snapshot them by value so any future
          // in-place edit optimization cannot silently poison undo history.
          curveEditVertices: state.editMode.curveEditVertices
            ? new Float32Array(state.editMode.curveEditVertices)
            : undefined,
          granularity: state.editMode.granularity,
          xray: state.editMode.xray,
          showVertexGroupColors: state.editMode.showVertexGroupColors,
          isCurve: state.editMode.isCurve,
        }
      : undefined,
    cursor3D: {
      position: [
        state.cursor3D.position[0],
        state.cursor3D.position[1],
        state.cursor3D.position[2],
      ],
    },
    sketch: cloneSceneSketchState(state.sketch),
    // Inking raster: clone the buffer so user edits after this snapshot don't
    // mutate the undo entry. Skipped when no raster exists (viewing mode with
    // no bake, or any non-inking workspace).
    inkingRaster: state.inkingMode?.rasterBuffer
      ? {
          buffer: new Uint8ClampedArray(state.inkingMode.rasterBuffer),
          width: state.inkingMode.rasterWidth,
          height: state.inkingMode.rasterHeight,
          dirtyFromBake: state.inkingMode.rasterDirtyFromBake,
        }
      : undefined,
  };
}

export function applySnapshot(
  state: AppState,
  snap: UndoableSnapshot,
): Partial<AppState> {
  clearLiveEditPreview();
  const patch: Partial<AppState> = {
    activeEditSession: null,
    shapeKeyWeights: cloneWeights(snap.shapeKeyWeights),
    referenceLayers: snap.referenceLayers.map((l) => ({
      ...l,
      offset: [...l.offset] as [number, number],
      vanishingLines: l.vanishingLines?.map((vl) => ({
        ...vl,
        start: [...vl.start] as [number, number],
        end: [...vl.end] as [number, number],
      })),
    })),
    activeReferenceLayerId: snap.activeReferenceLayerId,
    modifierOverrides: cloneOverrides(snap.modifierOverrides),
  };

  // Restore blend file entries from the snapshot (handles add/remove undo).
  // Restore group transforms from the snapshot.
  if (snap.blendFiles.size > 0) {
    const newFiles = new Map(snap.blendFiles);
    for (const [entryId, entry] of snap.blendFiles) {
      const gt = snap.groupTransforms.get(entryId);
      if (gt) {
        newFiles.set(entryId, {
          ...entry,
          groupTransform: {
            position: [...gt.position] as [number, number, number],
            rotation: [...gt.rotation] as [number, number, number, number],
            scale: [...gt.scale] as [number, number, number],
          },
        });
      }
    }
    patch.blendFiles = newFiles;
  } else {
    patch.blendFiles = new Map();
  }

  // Fully restore scene objects from snapshot (handles add/delete undo).
  // Pass through `cloneSceneObject` so the live store gets fresh buffers
  // detached from the snapshot's arrays — without this, the next sculpt
  // stroke would mutate the snapshot in place and corrupt the redo entry.
  patch.sceneObjects = restoreSceneObjectMap(
    state.sceneObjects,
    snap.sceneObjects,
    { materialize: (o) => cloneSceneObject(o) },
  );

  // Restore materials
  const materials = new Map<
    string,
    import("../modeling/types.ts").MaterialDataBlock
  >();
  for (const m of snap.materials) {
    materials.set(m.id, {
      id: m.id,
      name: m.name,
      properties: { ...m.properties },
    });
  }
  patch.materials = materials;

  // Restore mannequin morphs and bone poses
  patch.mannequinMorphs = cloneWeights(snap.mannequinMorphs);
  patch.mannequinBonePoses = cloneBonePoses(snap.mannequinBonePoses);

  // Restore visibility
  patch.visibility = new Map(snap.visibility);

  // Restore locked bones
  patch.lockedBones = new Map(
    [...snap.lockedBones].map(([k, v]) => [k, new Set(v)]),
  );

  // Restore pinned bones
  patch.pinnedBones = new Map(
    [...snap.pinnedBones].map(([k, v]) => [
      k,
      new Map([...v].map(([b, p]) => [b, [...p] as [number, number, number]])),
    ]),
  );

  // Restore collections
  const collections = new Map<string, import("./storeTypes.ts").Collection>();
  for (const c of snap.collections) {
    collections.set(c.id, {
      id: c.id,
      name: c.name,
      transform: cloneGroupTransform(c.transform),
      objectIds: new Set(c.objectIds),
      ...(c.cutterRef
        ? { cutterRef: cloneCollectionCutterRef(c.cutterRef) }
        : {}),
      ...(c.generatorRef
        ? { generatorRef: cloneCollectionGeneratorRef(c.generatorRef) }
        : {}),
    });
  }
  patch.collections = collections;

  // Restore bone volumes
  patch.boneVolumes = cloneWeights(snap.boneVolumes);

  // Handle edit mode transitions: the snapshot's editSelection determines
  // whether undo/redo should land in edit mode or object mode, so we always
  // follow the snapshot's intent regardless of the current mode.
  if (snap.editSelection) {
    patch.editMode = {
      objectId: snap.editSelection.objectId,
      selectedVertices: new Set(snap.editSelection.selectedVertices),
      selectedEdges: new Set(snap.editSelection.selectedEdges ?? []),
      selectedFaces: new Set(snap.editSelection.selectedFaces),
      hiddenVertices: new Set(snap.editSelection.hiddenVertices ?? []),
      hiddenFaces: new Set(snap.editSelection.hiddenFaces ?? []),
      granularity: snap.editSelection.granularity ?? "vertex",
      xray: snap.editSelection.xray ?? false,
      showVertexGroupColors: snap.editSelection.showVertexGroupColors ?? true,
      isCurve: snap.editSelection.isCurve,
      lastActiveVertex: null,
      lastActiveEdge: null,
      lastActiveFace: null,
      ...(snap.editSelection.curveEditVertices
        ? {
            curveEditVertices: new Float32Array(
              snap.editSelection.curveEditVertices,
            ),
          }
        : {}),
    };
    patch.mode = "edit" as InteractionMode;
    patch.workspaceMode = "edit" as WorkspaceMode;
  } else if (state.editMode) {
    // Snapshot was taken outside edit mode — exit edit mode on apply
    patch.editMode = null;
    patch.mode = "navigate" as InteractionMode;
    patch.workspaceMode = "object" as WorkspaceMode;
    patch.activeOperator = null;
  }

  // Restore the 3D cursor position. `selected` is transient UI state; we
  // carry the live flag through rather than resetting it so an in-progress
  // cursor drag isn't torn down by unrelated undo events.
  if (snap.cursor3D) {
    patch.cursor3D = {
      position: [
        snap.cursor3D.position[0],
        snap.cursor3D.position[1],
        snap.cursor3D.position[2],
      ],
      selected: state.cursor3D.selected,
    };
  }

  // Restore scene-level sketch state. Deep-clone so the next sketch mutation
  // doesn't reach back into the undo entry's arrays.
  patch.sketch = cloneSceneSketchState(snap.sketch);
  // Bump sketchStrokeSeq so overlays re-enumerate their stroke lists; the
  // restored state has different stroke identities even when the layer count
  // is unchanged.
  patch.sketchStrokeSeq = state.sketchStrokeSeq + 1;

  // Restore selection targets from the snapshot, filtering out any that no
  // longer exist in the restored scene (e.g. objects added after the snapshot).
  const restoredIds = new Set(snap.sceneObjects.map((o) => o.id));
  const validTargets = snap.selectedObjectTargets.filter((target) => {
    switch (target.kind) {
      case "sceneObject":
        return restoredIds.has(target.id);
      case "blendEntry":
        return snap.blendFiles.has(target.id);
      case "collection":
        return patch.collections?.has(target.id) ?? false;
      default:
        return false;
    }
  });
  // When landing in edit mode via undo/redo, pin the selection to the
  // edited object — takes precedence over the snapshot's general selection.
  patch.selectedObjectTargets = snap.editSelection
    ? [{ kind: "sceneObject", id: snap.editSelection.objectId }]
    : validTargets;

  // Inking raster: replace only when the live store still has an inkingMode
  // active (otherwise the snapshot came from a session where we're no longer
  // in inking mode and there's nowhere to apply it).
  if (state.inkingMode) {
    if (snap.inkingRaster) {
      patch.inkingMode = {
        ...state.inkingMode,
        rasterBuffer: new Uint8ClampedArray(snap.inkingRaster.buffer),
        rasterWidth: snap.inkingRaster.width,
        rasterHeight: snap.inkingRaster.height,
        rasterSeq: state.inkingMode.rasterSeq + 1,
        rasterDirtyFromBake: snap.inkingRaster.dirtyFromBake,
      };
    } else {
      // Snapshot preceded any bake — clear the live raster so undo-before-bake
      // actually removes the sketch.
      patch.inkingMode = {
        ...state.inkingMode,
        rasterBuffer: null,
        rasterWidth: 0,
        rasterHeight: 0,
        rasterSeq: state.inkingMode.rasterSeq + 1,
        rasterDirtyFromBake: false,
      };
    }
  }

  // Rebuild driver graph if blend files changed (e.g. undo of removeBlendFile)
  const blendFilesChanged = snap.blendFiles !== state.blendFiles;
  if (blendFilesChanged && snap.blendFiles.size > 0) {
    const mergedSKS = allShapeKeySets(snap.blendFiles);
    if (mergedSKS.size > 0) {
      const driverGraph = new DriverGraph();
      driverGraph.build(mergedSKS);
      driverGraph.evaluate(patch.shapeKeyWeights!);
      patch.driverGraph = driverGraph;
    } else {
      patch.driverGraph = null;
    }
  } else if (blendFilesChanged && snap.blendFiles.size === 0) {
    patch.driverGraph = null;
  } else if (state.driverGraph) {
    // Blend files unchanged — just re-evaluate existing graph
    state.driverGraph.evaluate(patch.shapeKeyWeights!);
  }

  return patch;
}

/** Type guard to distinguish sculpt deltas from full snapshots. */
export function isSculptDelta(entry: UndoEntry): entry is SculptDelta {
  return "type" in entry && (entry as SculptDelta).type === "sculpt-delta";
}

/** Type guard for selection-only undo entries (lightweight delta). */
export function isSelectionDelta(entry: UndoEntry): entry is SelectionDelta {
  return (
    "type" in entry && (entry as SelectionDelta).type === "selection-delta"
  );
}

export function isVertexPositionDelta(
  entry: UndoEntry,
): entry is VertexPositionDelta {
  return (
    "type" in entry &&
    (entry as VertexPositionDelta).type === "vertex-position-delta"
  );
}

export function isObjectGeometrySnapshotDelta(
  entry: UndoEntry,
): entry is ObjectGeometrySnapshotDelta {
  return (
    "type" in entry &&
    (entry as ObjectGeometrySnapshotDelta).type === "object-geometry-snapshot"
  );
}

/**
 * Snapshot the selection-related slice of state. Cheap (O(selection-size)
 * not O(scene-size)) — captures only what selection-only call
 * sites can mutate. Mode transitions and geometry edits stay on the
 * full-snapshot path.
 */
export function captureSelectionDeltaState(
  state: AppState,
): SelectionDeltaState {
  const em = state.editMode;
  return {
    selectedObjectTargets: state.selectedObjectTargets.map((t) => ({ ...t })),
    cursor3DSelected: state.cursor3D.selected,
    lastObjectModeSelection: state.lastObjectModeSelection
      ? new Set(state.lastObjectModeSelection)
      : null,
    editSelection: em
      ? {
          objectId: em.objectId,
          selectedVertices: new Set(em.selectedVertices),
          selectedEdges: new Set(em.selectedEdges),
          selectedFaces: new Set(em.selectedFaces),
          lastActiveVertex: em.lastActiveVertex,
          lastActiveEdge: em.lastActiveEdge,
          lastActiveFace: em.lastActiveFace,
        }
      : null,
    lastEditModeSelection: state.lastEditModeSelection
      ? {
          objectId: state.lastEditModeSelection.objectId,
          selectedVertices: new Set(
            state.lastEditModeSelection.selectedVertices,
          ),
          selectedEdges: new Set(state.lastEditModeSelection.selectedEdges),
          selectedFaces: new Set(state.lastEditModeSelection.selectedFaces),
          granularity: state.lastEditModeSelection.granularity,
        }
      : null,
    mode: state.mode,
  };
}

/**
 * Build the AppState patch that restores a SelectionDeltaState. Caller
 * passes the side to apply (`before` for undo, `after` for redo).
 *
 * The edit-mode preservation rule: SelectionDelta only flows through
 * call sites that are within a single mode (no enter/exit edit-mode
 * happens through them), so restoring `editSelection` always merges
 * with the current `editMode` slice rather than replacing it. If the
 * user has somehow exited edit mode between push and undo (which would
 * require a snapshot push in between), the `editSelection` write is
 * skipped — the snapshot above it would have already restored mode.
 */
export function applySelectionDeltaState(
  state: AppState,
  side: SelectionDeltaState,
): Partial<AppState> {
  const patch: Partial<AppState> = {
    selectedObjectTargets: side.selectedObjectTargets.map((t) => ({ ...t })),
    cursor3D: { ...state.cursor3D, selected: side.cursor3DSelected },
    lastObjectModeSelection: side.lastObjectModeSelection
      ? new Set(side.lastObjectModeSelection)
      : null,
    lastEditModeSelection: side.lastEditModeSelection
      ? {
          objectId: side.lastEditModeSelection.objectId,
          selectedVertices: new Set(
            side.lastEditModeSelection.selectedVertices,
          ),
          selectedEdges: new Set(side.lastEditModeSelection.selectedEdges),
          selectedFaces: new Set(side.lastEditModeSelection.selectedFaces),
          granularity: side.lastEditModeSelection.granularity,
        }
      : null,
    mode: side.mode,
  };
  if (side.editSelection && state.editMode) {
    patch.editMode = {
      ...state.editMode,
      objectId: side.editSelection.objectId,
      selectedVertices: new Set(side.editSelection.selectedVertices),
      selectedEdges: new Set(side.editSelection.selectedEdges),
      selectedFaces: new Set(side.editSelection.selectedFaces),
      lastActiveVertex: side.editSelection.lastActiveVertex,
      lastActiveEdge: side.editSelection.lastActiveEdge,
      lastActiveFace: side.editSelection.lastActiveFace,
    };
  }
  return patch;
}

/** Push a selection-only delta. Both directions are baked in so undo
 *  and redo never need to capture a snapshot for a pure selection
 *  change — the dispatch in `undo()` / `redo()` recycles the same
 *  delta entry between the two stacks. */
export function pushSelectionUndo(
  before: SelectionDeltaState,
  after: SelectionDeltaState,
): void {
  undoManager.push({ type: "selection-delta", before, after });
}

/**
 * Apply a sculpt delta to the current state (for undo).
 * Writes the "before" values back to the store's scene object arrays.
 */
export function applySculptDeltaUndo(
  state: AppState,
  delta: SculptDelta,
): Partial<AppState> {
  return applySculptDeltaSide(state, delta, "before");
}

/**
 * Apply a sculpt delta forward (for redo).
 * Writes the "after" values back to the store's scene object arrays.
 */
export function applySculptDeltaRedo(
  state: AppState,
  delta: SculptDelta,
): Partial<AppState> {
  return applySculptDeltaSide(state, delta, "after");
}

function applySculptDeltaSide(
  state: AppState,
  delta: SculptDelta,
  side: "before" | "after",
): Partial<AppState> {
  const obj = state.sceneObjects.get(delta.objectId);
  if (!obj) return {};

  const verts = side === "before" ? delta.beforeVertices : delta.afterVertices;

  // Write sparse vertex positions into the store arrays
  const newVertices = obj.vertices.slice();
  for (let i = 0; i < delta.affected.length; i++) {
    const off = delta.affected[i] * 3;
    const src = i * 3;
    newVertices[off] = verts[src];
    newVertices[off + 1] = verts[src + 1];
    newVertices[off + 2] = verts[src + 2];
  }

  // Recompute per-loop normals from restored positions rather than
  // sparse-writing per-vertex normal data into the per-corner buffer.
  const newNormals = cloneLoopNormals(obj.loopNormals);
  if (obj.topology) {
    const affectedArr = Array.from(delta.affected);
    recomputeLoopNormalsPartial(
      newVertices,
      newNormals,
      affectedArr,
      obj.topology,
      resolveAutoSmoothAngle(obj.shading, obj.autoSmoothAngle),
    );
  }

  const patchedObj = {
    ...obj,
    vertices: newVertices,
    loopNormals: newNormals,
  };
  const newSceneObjects = new Map(state.sceneObjects);
  newSceneObjects.set(delta.objectId, patchedObj);

  return {
    sceneObjects: newSceneObjects,
    sculptStrokeSeq: state.sculptStrokeSeq + 1,
  };
}

export function applyObjectGeometrySnapshotUndo(
  state: AppState,
  delta: ObjectGeometrySnapshotDelta,
): Partial<AppState> {
  return applyObjectGeometryHistoryState(state, delta.before);
}

export function applyObjectGeometrySnapshotRedo(
  state: AppState,
  delta: ObjectGeometrySnapshotDelta,
): Partial<AppState> {
  return applyObjectGeometryHistoryState(state, delta.after);
}

export function applyVertexPositionDeltaUndo(
  state: AppState,
  delta: VertexPositionDelta,
): Partial<AppState> {
  return applyVertexPositionDeltaSide(state, delta, "before");
}

export function applyVertexPositionDeltaRedo(
  state: AppState,
  delta: VertexPositionDelta,
): Partial<AppState> {
  return applyVertexPositionDeltaSide(state, delta, "after");
}

function applyVertexPositionDeltaSide(
  state: AppState,
  delta: VertexPositionDelta,
  side: "before" | "after",
): Partial<AppState> {
  const obj = state.sceneObjects.get(delta.objectId);
  if (!obj) return {};

  const srcVertices =
    side === "before" ? delta.beforeVertices : delta.afterVertices;
  const srcBase =
    side === "before" ? delta.beforeBaseVertices : delta.afterBaseVertices;
  const editSelection =
    side === "before" ? delta.beforeEditSelection : delta.afterEditSelection;

  const newVertices = obj.vertices.slice();
  for (let i = 0; i < delta.affected.length; i++) {
    const vi = delta.affected[i]!;
    const src = i * 3;
    const dst = vi * 3;
    newVertices[dst] = srcVertices[src]!;
    newVertices[dst + 1] = srcVertices[src + 1]!;
    newVertices[dst + 2] = srcVertices[src + 2]!;
  }

  const affectedSet = new Set<number>();
  for (const vi of delta.affected) affectedSet.add(vi);

  const patch: Partial<import("../modeling/types.ts").SceneObject> = {
    vertices: newVertices,
  };

  if (
    srcBase &&
    obj.baseVertices &&
    obj.baseVertices.length === newVertices.length
  ) {
    const newBase = obj.baseVertices.slice();
    for (let i = 0; i < delta.affected.length; i++) {
      const vi = delta.affected[i]!;
      const src = i * 3;
      const dst = vi * 3;
      newBase[dst] = srcBase[src]!;
      newBase[dst + 1] = srcBase[src + 1]!;
      newBase[dst + 2] = srcBase[src + 2]!;
    }
    patch.baseVertices = newBase;
  }

  const angle = resolveAutoSmoothAngle(obj.shading, obj.autoSmoothAngle);
  const vertexCount = newVertices.length / 3;
  const canPartial =
    delta.affected.length > 0 &&
    delta.affected.length <= vertexCount * 0.5 &&
    obj.loopNormals.length === obj.topology.corners.length * 3;
  if (canPartial) {
    const newNormals = cloneLoopNormals(obj.loopNormals);
    recomputeLoopNormalsPartial(
      newVertices,
      newNormals,
      Array.from(delta.affected),
      obj.topology,
      angle,
    );
    patch.loopNormals = newNormals;
    if (obj.uvs && obj.uvs.length === obj.topology.corners.length * 2) {
      const newUvs = obj.uvs.slice();
      unwrapBoxProjectPartial(
        newVertices,
        newNormals,
        obj.topology,
        affectedSet,
        newUvs,
      );
      patch.uvs = newUvs;
    }
  } else {
    const loopNormals = recomputeLoopNormals(newVertices, obj.topology, angle);
    patch.loopNormals = loopNormals;
    patch.uvs = unwrapBoxProject(newVertices, loopNormals, obj.topology);
  }

  const sceneObjects = new Map(state.sceneObjects);
  sceneObjects.set(delta.objectId, { ...obj, ...patch });
  return {
    sceneObjects,
    editMode: editModeFromSnapshot(editSelection),
    mode: "edit" as InteractionMode,
    workspaceMode: "edit" as WorkspaceMode,
    selectedObjectTargets: [{ kind: "sceneObject", id: delta.objectId }],
  };
}

// ---------------------------------------------------------------------------
// Undo memory budget
//
// A flat entry-count cap is byte-blind: a 1 M-face scene's snapshots are
// ~500x larger than a 2 k-face scene's, so a fixed 100-entry stack consumes
// radically different memory across scenes -- enough to OOM constrained
// devices. We cap by retained bytes instead, with a user-configurable
// budget exposed in Settings. `minEntries` ensures undo is never blocked
// even for extreme scene sizes: we always retain at least one prior state.
// ---------------------------------------------------------------------------

const MB = 1024 * 1024;
// Browsers reload tabs under memory pressure well before 1 GB on mobile and
// 2-4 GB on desktop, so the budget must stay comfortably inside those
// envelopes. 256 MB is a safe cross-device default; 32 MB is the lowest
// explicit opt-in for constrained devices; 2 GB is the ceiling a power user
// can opt into on desktop without inviting tab kills.
export const UNDO_BUDGET_DEFAULT_BYTES = 256 * MB;
export const UNDO_BUDGET_MIN_BYTES = 32 * MB;
export const UNDO_BUDGET_MAX_BYTES = 2 * 1024 * MB;
/** Allowed options surfaced in Settings. */
export const UNDO_BUDGET_OPTIONS: Array<{ label: string; bytes: number }> = [
  { label: "Minimal (32 MB)", bytes: 32 * MB },
  { label: "Shallow (256 MB)", bytes: 256 * MB },
  { label: "Normal (512 MB)", bytes: 512 * MB },
  { label: "Deep (1 GB)", bytes: 1024 * MB },
  { label: "Maximum (2 GB)", bytes: 2 * 1024 * MB },
];

function readPersistedBudget(): number {
  try {
    const raw = Number(localStorage.getItem("poseref-undo-budget-bytes"));
    if (
      Number.isFinite(raw) &&
      raw >= UNDO_BUDGET_MIN_BYTES &&
      raw <= UNDO_BUDGET_MAX_BYTES
    ) {
      return raw;
    }
  } catch {
    /* ignore */
  }
  return UNDO_BUDGET_DEFAULT_BYTES;
}

export function persistUndoBudget(bytes: number): void {
  try {
    localStorage.setItem("poseref-undo-budget-bytes", String(bytes));
  } catch {
    /* ignore */
  }
}

/**
 * Estimate the retained bytes of an undo entry. Dominated by typed-array
 * payloads on SceneObjects (positions, normals, indices, topology arrays,
 * morphs, skin weights). Container overhead (Maps, Sets, plain objects) is
 * approximated with a small constant per entry -- good enough for the
 * eviction decision, which only needs relative accuracy.
 */
export function estimateEntryBytes(entry: UndoEntry): number {
  return estimateRetainedBytes(entry);
}

export function estimateRetainedBytes(value: unknown): number {
  return estimateBytes(value, 0);
}

function estimateBytes(v: unknown, depth: number): number {
  if (v == null) return 0;
  if (depth > 6) return 64;
  const t = typeof v;
  if (t === "number" || t === "boolean") return 8;
  if (t === "bigint") return 16;
  if (t === "string") return (v as string).length * 2 + 16;
  if (t !== "object") return 16;

  if (ArrayBuffer.isView(v)) return (v as ArrayBufferView).byteLength;
  if (Array.isArray(v)) {
    let s = 32;
    for (const x of v) s += estimateBytes(x, depth + 1);
    return s;
  }
  if (v instanceof Map) {
    let s = 32;
    for (const [k, vv] of v as Map<unknown, unknown>) {
      s += estimateBytes(k, depth + 1) + estimateBytes(vv, depth + 1);
    }
    return s;
  }
  if (v instanceof Set) {
    let s = 32;
    for (const x of v as Set<unknown>) s += estimateBytes(x, depth + 1);
    return s;
  }
  // Plain object: fixed per-object overhead plus recursion.
  let s = 64;
  for (const k in v as object) {
    s += estimateBytes((v as Record<string, unknown>)[k], depth + 1);
  }
  return s;
}

export const undoManager = new UndoManager<UndoEntry>({
  maxEntries: 100,
  minEntries: 1,
  maxBytes: readPersistedBudget(),
  sizeOf: estimateEntryBytes,
});

/** Expose the undo manager for persistence layer access. */
export function getUndoManager() {
  return undoManager;
}

// ---------------------------------------------------------------------------
// The undo history is universal across modes (matching Blender's behavior).
// Every snapshot captures editSelection when in edit mode, allowing undo/redo
// to cross mode boundaries — undoing back through an edit session re-enters
// edit mode, and redoing forward exits it again automatically.
// ---------------------------------------------------------------------------
